/**
 * <reborn-hero-video> – bestemmer om bakgrunnsvideoen i Om Re-Born-introen
 * i det hele tatt skal lastes ned.
 *
 * Videoen ligger i en <template> i markupen og settes først inn i DOM-en når
 * betingelsene er oppfylt. Poenget er at `display: none` i CSS ikke hindrer
 * nedlasting – nettleseren henter filen uansett. Skal videoen faktisk ikke
 * belaste mobil eller en bruker med redusert bevegelse, må elementet aldri
 * opprettes.
 *
 * Reservebildet ligger som et vanlig <img> under videoen og krever ingen
 * JavaScript. Kjører ikke dette skriptet, eller feiler videoen, blir bildet
 * stående – seksjonen er aldri avhengig av at skriptet lykkes.
 *
 * Egendefinert element (ikke inline-skript) fordi Theme Editor bytter ut
 * seksjonens HTML ved hver endring. Inline-skript kjøres ikke på nytt da,
 * mens et egendefinert element oppgraderes automatisk hver gang det settes
 * inn i dokumentet.
 */
class RebornHeroVideo extends HTMLElement {
  connectedCallback() {
    // Theme Editor kan sette inn seksjonen på nytt; da skal ikke videoen dupliseres.
    if (this.dataset.mounted === 'true') return;

    const template = this.querySelector('template');
    if (!template) return;

    const skip = this.skipReason();
    if (skip) {
      this.setAttribute('data-skipped', skip);
      return;
    }

    this.appendChild(template.content.cloneNode(true));
    this.dataset.mounted = 'true';

    const video = this.querySelector('video');
    if (!video) return;

    // Reservebildet ligger synlig til videoen faktisk viser bilde. Uten dette
    // ville det blinket svart i det videoelementet er opprettet, men før
    // første bilderute er dekodet.
    video.addEventListener('playing', () => this.setAttribute('data-playing', ''), { once: true });

    // Slår videoen feil (kodek, nettverk, autoplay blokkert), fjernes den igjen
    // slik at reservebildet står alene i stedet for en tom, svart flate.
    video.addEventListener(
      'error',
      () => {
        video.remove();
        this.removeAttribute('data-playing');
        this.setAttribute('data-skipped', 'error');
      },
      { once: true }
    );

    // Enkelte nettlesere avviser autoplay selv når videoen er lydløs.
    const played = video.play();
    if (played && typeof played.catch === 'function') {
      played.catch(() => {
        video.remove();
        this.setAttribute('data-skipped', 'autoplay-blocked');
      });
    }
  }

  /** @returns {string} tom streng når videoen skal spilles, ellers årsaken. */
  skipReason() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 'reduced-motion';

    // Sparemodus og svært langsomme forbindelser. Network Information API
    // finnes ikke i alle nettlesere, så fravær tolkes som "greit å spille".
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {
      if (connection.saveData === true) return 'save-data';
      if (/^(slow-2g|2g)$/.test(connection.effectiveType || '')) return 'slow-connection';
    }

    const allowMobile = this.dataset.mobile === 'true';
    if (!allowMobile && window.matchMedia('(max-width: 749px)').matches) return 'mobile-disabled';

    return '';
  }
}

if (!customElements.get('reborn-hero-video')) {
  customElements.define('reborn-hero-video', RebornHeroVideo);
}
